"use client";

import React, { useState, useCallback } from 'react';
import styles from './_gc-step-most-recent-labs.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputCheckbox } from '@/components/form';

/**
 * @description GC OPTIMIZE™ Calculator — Most Recent Labs
 * Shown for pediatric patients on the "Patient already on CRENESSITY" path.
 * Introduces A4 and GC dose context, then asks the HCP to classify
 * the patient's current lab status with three options:
 *   - A4 ~ goal
 *   - A4 > goal
 *   - A4 < goal + GC dose > physiologic + prior reduction tolerated
 *
 * Inner steps:
 *   0 — Radio selection (must pick one to advance)
 *   1 — Shows recommendation text, then Save & continue dispatches
 *
 * Saved fields (via dispatch):
 *   a4Assessment — 'at-goal' | 'above-goal' | 'below-goal'
 *
 * @param {Object}   state    — wizard state from useReducer
 * @param {Function} dispatch — wizard dispatch
 * @param {Function} onBack   — navigate to previous step
 * @param {Function} onNext   — navigate to next step
 */
function GcStepMostRecentLabs({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  const [a4Assessment, setA4Assessment] = useState(state.formValues.a4Assessment ?? '');
  const [innerStep, setInnerStep] = useState(0);

  // Reset inner step when selection changes so the path recalculates cleanly
  const handleSelectionChange = (value) => {
    setA4Assessment(value);
    setInnerStep(0);
  };

  const isFinalInnerStep = innerStep === 1;

  // at-goal and below-goal show a recommendation inner step; above-goal saves directly
  const hasInnerStep = a4Assessment === 'at-goal' || a4Assessment === 'below-goal';

  const handleNextOrSave = useCallback(() => {
    if (hasInnerStep && innerStep < 1) {
      setInnerStep(1);
    } else {
      const a4Changed = a4Assessment !== state.formValues.a4Assessment;
      dispatch({ type: 'SAVE_MOST_RECENT_LABS', a4Assessment });
      onNext({ formValues: { ...state.formValues, a4Assessment }, ...(a4Changed ? { forceReadvance: true } : {}) });
    }
  }, [innerStep, hasInnerStep, dispatch, a4Assessment, onNext, state.formValues]);

  const isSaveStep = hasInnerStep ? isFinalInnerStep : true;

  const recommendationText = a4Assessment === 'below-goal'
    ? 'Reduce daily GC dose by 10% to 20% in ~ 1 to 3 months.'
    : 'Consider maintaining current GC dose and assessing treatment progress ~3 to 4 months after initiating CRENESSITY.';

  return (
    <GcStepShell
      title="Most Recent Labs"
      showBack={false}
      onBack={onBack}
      onNext={handleNextOrSave}
      isNextDisabled={!a4Assessment}
      isReview={isReview}
      nextLabel={isSaveStep ? 'Save & continue' : 'Next'}
      nextVariant={isSaveStep ? ['solid', 'solid--eggplant'] : ['solid', 'solid--orange']}
      nextIconType={isSaveStep ? undefined : 'arrow-right'}
      stepNumber={stepNumber}
      innerStep={innerStep}
    >
      <div className={styles.most_recent_labs}>
        <p className={styles.most_recent_labs_intro}>
          Knowing your patient&apos;s goal A4 and GC values is key to adjusting GC doses.
        </p>

        <p className={styles.most_recent_labs_body}>
          For growing patients, the A4 treatment goal should generally be <span
            className="text-nowrap">1-1.5x ULN</span> for sex and either age or pubertal stage for morning pre-GC
          measurements, or &le;ULN for post-GC measurements.
        </p>

        <p className={styles.most_recent_labs_body}>
          For GC doses, 8 to 11 mg/m&sup2;/day HCe may be sufficient for cortisol
          replacement in most pediatric patients; however, some patients may require
          higher doses to prevent adrenal insufficiency. Symptoms of adrenal insufficiency overlap with those 
          for GC withdrawal (ie, when reducing from a higher to a lower supraphysiologic dose) and may include 
          headache, fatigue, abdominal pain, and/or nausea.
        </p>

        <fieldset className={styles.field_group}>
          <legend className={styles.field_label}>What are your patient&apos;s current labs?</legend>

          <FormInputCheckbox
            name="a4-at-goal"
            typeRadio
            radioGroupName="a4-assessment"
            defaultChecked={a4Assessment === 'at-goal'}
            onChange={() => handleSelectionChange('at-goal')}
          >
            Patient&apos;s A4 level is ~ goal
          </FormInputCheckbox>

          <FormInputCheckbox
            name="a4-below-goal"
            typeRadio
            radioGroupName="a4-assessment"
            defaultChecked={a4Assessment === 'below-goal'}
            onChange={() => handleSelectionChange('below-goal')}
          >
            Patient&apos;s A4 level is &lt; goal and GC dose &gt; physiologic and previous GC dose reduction was tolerated
          </FormInputCheckbox>

          <FormInputCheckbox
            name="a4-above-goal"
            typeRadio
            radioGroupName="a4-assessment"
            defaultChecked={a4Assessment === 'above-goal'}
            onChange={() => handleSelectionChange('above-goal')}
          >
            Patient&apos;s A4 is &gt; goal
          </FormInputCheckbox>
        </fieldset>

        {isFinalInnerStep && hasInnerStep && (
          <p className={styles.recommendation}>
            {recommendationText}
          </p>
        )}
      </div>
    </GcStepShell>
  );
}

export default GcStepMostRecentLabs;
